/**
 * AI Governance Redux Slice - APB-1965
 *
 * State management for all AI governance features:
 * - Governance summary & dashboard
 * - Agent listing with filters
 * - Containment management
 * - Forensics investigation
 * - Regulatory notifications
 * - Shadow agent discovery
 * - Report generation
 */

import { createSlice, createAsyncThunk, createSelector, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../../store/index.js';
import {
  getGovernanceSummary,
  listGovernanceAgents,
  requestContainment,
  getAgentForensics,
  createRegulatoryNotification,
  updateNotificationStatus,
  listShadowAgents,
  generateReport,
} from '../services/aiGovernanceApi.js';

import type {
  ContainmentRequest,
  ContainmentResponse,
  ForensicTimeline,
  GovernanceAgent,
  GovernanceAgentFilters,
  GovernanceAgentListResponse,
  GovernanceReport,
  GovernanceReportRequest,
  GovernanceShadowAgent,
  GovernanceSummary,
  NotificationStatusUpdate,
  RegulatoryNotification,
  RegulatoryNotificationRequest,
} from '../types/index.js';

// =============================================================================
// STATE INTERFACE
// =============================================================================

export interface AIGovernanceState {
  // Summary & Dashboard
  summary: GovernanceSummary | null;
  summaryLoading: boolean;
  summaryError: string | null;

  // Agents
  agents: GovernanceAgent[];
  agentsTotal: number;
  agentsLoading: boolean;
  agentsError: string | null;
  agentFilters: GovernanceAgentFilters;

  // Containment
  containmentLoading: boolean;
  containmentError: string | null;
  lastContainmentResponse: ContainmentResponse | null;

  // Forensics
  selectedAgentForensics: ForensicTimeline | null;
  forensicsLoading: boolean;
  forensicsError: string | null;

  // Notifications
  notifications: RegulatoryNotification[];
  notificationsLoading: boolean;
  notificationsError: string | null;

  // Shadow Agents
  shadowAgents: GovernanceShadowAgent[];
  shadowAgentsLoading: boolean;
  shadowAgentsError: string | null;

  // Reports
  generatedReports: GovernanceReport[];
  reportGenerating: boolean;
  reportError: string | null;
}

const initialState: AIGovernanceState = {
  summary: null,
  summaryLoading: false,
  summaryError: null,

  agents: [],
  agentsTotal: 0,
  agentsLoading: false,
  agentsError: null,
  agentFilters: {
    limit: 50,
    offset: 0,
  },

  containmentLoading: false,
  containmentError: null,
  lastContainmentResponse: null,

  selectedAgentForensics: null,
  forensicsLoading: false,
  forensicsError: null,

  notifications: [],
  notificationsLoading: false,
  notificationsError: null,

  shadowAgents: [],
  shadowAgentsLoading: false,
  shadowAgentsError: null,

  generatedReports: [],
  reportGenerating: false,
  reportError: null,
};

// =============================================================================
// ASYNC THUNKS
// =============================================================================

export const fetchGovernanceSummary = createAsyncThunk(
  'aiGovernance/fetchGovernanceSummary',
  async (_, { rejectWithValue }) => {
    try {
      return await getGovernanceSummary();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch governance summary');
    }
  },
);

export const fetchGovernanceAgents = createAsyncThunk(
  'aiGovernance/fetchGovernanceAgents',
  async (filters: GovernanceAgentFilters | undefined, { rejectWithValue }) => {
    try {
      return await listGovernanceAgents(filters);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch governance agents');
    }
  },
);

export const performContainment = createAsyncThunk(
  'aiGovernance/performContainment',
  async (request: ContainmentRequest, { rejectWithValue }) => {
    try {
      return await requestContainment(request);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to perform containment');
    }
  },
);

export const fetchAgentForensics = createAsyncThunk(
  'aiGovernance/fetchAgentForensics',
  async (params: { agentId: string; hoursBack?: number }, { rejectWithValue }) => {
    try {
      return await getAgentForensics(params.agentId, params.hoursBack);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch forensics timeline');
    }
  },
);

export const createNotification = createAsyncThunk(
  'aiGovernance/createNotification',
  async (request: RegulatoryNotificationRequest, { rejectWithValue }) => {
    try {
      return await createRegulatoryNotification(request);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create notification');
    }
  },
);

export const updateNotification = createAsyncThunk(
  'aiGovernance/updateNotification',
  async (
    params: { notificationId: string; update: NotificationStatusUpdate },
    { rejectWithValue },
  ) => {
    try {
      return await updateNotificationStatus(params.notificationId, params.update);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update notification status');
    }
  },
);

export const fetchShadowAgents = createAsyncThunk(
  'aiGovernance/fetchShadowAgents',
  async (_, { rejectWithValue }) => {
    try {
      return await listShadowAgents();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch shadow agents');
    }
  },
);

export const generateGovernanceReport = createAsyncThunk(
  'aiGovernance/generateGovernanceReport',
  async (request: GovernanceReportRequest, { rejectWithValue }) => {
    try {
      return await generateReport(request);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to generate report');
    }
  },
);

// =============================================================================
// SLICE
// =============================================================================

const aiGovernanceSlice = createSlice({
  name: 'aiGovernance',
  initialState,
  reducers: {
    // Summary actions
    clearSummaryError: (state) => {
      state.summaryError = null;
    },

    // Agent actions
    setAgentFilters: (state, action: PayloadAction<GovernanceAgentFilters>) => {
      state.agentFilters = { ...state.agentFilters, ...action.payload };
    },
    clearAgentsError: (state) => {
      state.agentsError = null;
    },

    // Containment actions
    clearContainmentError: (state) => {
      state.containmentError = null;
    },
    resetContainmentResponse: (state) => {
      state.lastContainmentResponse = null;
    },

    // Forensics actions
    clearForensicsError: (state) => {
      state.forensicsError = null;
    },
    clearSelectedForensics: (state) => {
      state.selectedAgentForensics = null;
    },

    // Notification actions
    clearNotificationsError: (state) => {
      state.notificationsError = null;
    },

    // Shadow agents actions
    clearShadowAgentsError: (state) => {
      state.shadowAgentsError = null;
    },

    // Report actions
    clearReportError: (state) => {
      state.reportError = null;
    },
  },

  extraReducers: (builder) => {
    // =========================================================================
    // Fetch Governance Summary
    // =========================================================================
    builder
      .addCase(fetchGovernanceSummary.pending, (state) => {
        state.summaryLoading = true;
        state.summaryError = null;
      })
      .addCase(fetchGovernanceSummary.fulfilled, (state, action) => {
        state.summaryLoading = false;
        state.summary = action.payload;
      })
      .addCase(fetchGovernanceSummary.rejected, (state, action) => {
        state.summaryLoading = false;
        state.summaryError =
          (action.payload as string) || action.error.message || 'Failed to fetch governance summary';
      });

    // =========================================================================
    // Fetch Governance Agents
    // =========================================================================
    builder
      .addCase(fetchGovernanceAgents.pending, (state) => {
        state.agentsLoading = true;
        state.agentsError = null;
      })
      .addCase(fetchGovernanceAgents.fulfilled, (state, action) => {
        state.agentsLoading = false;
        state.agents = action.payload.agents;
        state.agentsTotal = action.payload.total;
      })
      .addCase(fetchGovernanceAgents.rejected, (state, action) => {
        state.agentsLoading = false;
        state.agentsError =
          (action.payload as string) || action.error.message || 'Failed to fetch governance agents';
      });

    // =========================================================================
    // Perform Containment
    // =========================================================================
    builder
      .addCase(performContainment.pending, (state) => {
        state.containmentLoading = true;
        state.containmentError = null;
      })
      .addCase(performContainment.fulfilled, (state, action) => {
        state.containmentLoading = false;
        state.lastContainmentResponse = action.payload;
      })
      .addCase(performContainment.rejected, (state, action) => {
        state.containmentLoading = false;
        state.containmentError =
          (action.payload as string) || action.error.message || 'Failed to perform containment';
      });

    // =========================================================================
    // Fetch Agent Forensics
    // =========================================================================
    builder
      .addCase(fetchAgentForensics.pending, (state) => {
        state.forensicsLoading = true;
        state.forensicsError = null;
      })
      .addCase(fetchAgentForensics.fulfilled, (state, action) => {
        state.forensicsLoading = false;
        state.selectedAgentForensics = action.payload;
      })
      .addCase(fetchAgentForensics.rejected, (state, action) => {
        state.forensicsLoading = false;
        state.forensicsError =
          (action.payload as string) || action.error.message || 'Failed to fetch forensics timeline';
      });

    // =========================================================================
    // Create Regulatory Notification
    // =========================================================================
    builder
      .addCase(createNotification.pending, (state) => {
        state.notificationsLoading = true;
        state.notificationsError = null;
      })
      .addCase(createNotification.fulfilled, (state, action) => {
        state.notificationsLoading = false;
        state.notifications.push(action.payload);
      })
      .addCase(createNotification.rejected, (state, action) => {
        state.notificationsLoading = false;
        state.notificationsError =
          (action.payload as string) || action.error.message || 'Failed to create notification';
      });

    // =========================================================================
    // Update Notification Status
    // =========================================================================
    builder
      .addCase(updateNotification.pending, (state) => {
        state.notificationsLoading = true;
        state.notificationsError = null;
      })
      .addCase(updateNotification.fulfilled, (state, action) => {
        state.notificationsLoading = false;
        const index = state.notifications.findIndex(
          (n) => n.id === action.payload.id,
        );
        if (index !== -1) {
          state.notifications[index] = action.payload;
        }
      })
      .addCase(updateNotification.rejected, (state, action) => {
        state.notificationsLoading = false;
        state.notificationsError =
          (action.payload as string) || action.error.message || 'Failed to update notification status';
      });

    // =========================================================================
    // Fetch Shadow Agents
    // =========================================================================
    builder
      .addCase(fetchShadowAgents.pending, (state) => {
        state.shadowAgentsLoading = true;
        state.shadowAgentsError = null;
      })
      .addCase(fetchShadowAgents.fulfilled, (state, action) => {
        state.shadowAgentsLoading = false;
        state.shadowAgents = action.payload;
      })
      .addCase(fetchShadowAgents.rejected, (state, action) => {
        state.shadowAgentsLoading = false;
        state.shadowAgentsError =
          (action.payload as string) || action.error.message || 'Failed to fetch shadow agents';
      });

    // =========================================================================
    // Generate Governance Report
    // =========================================================================
    builder
      .addCase(generateGovernanceReport.pending, (state) => {
        state.reportGenerating = true;
        state.reportError = null;
      })
      .addCase(generateGovernanceReport.fulfilled, (state, action) => {
        state.reportGenerating = false;
        state.generatedReports.push(action.payload);
      })
      .addCase(generateGovernanceReport.rejected, (state, action) => {
        state.reportGenerating = false;
        state.reportError =
          (action.payload as string) || action.error.message || 'Failed to generate report';
      });
  },
});

// =============================================================================
// ACTIONS
// =============================================================================

export const {
  clearSummaryError,
  setAgentFilters,
  clearAgentsError,
  clearContainmentError,
  resetContainmentResponse,
  clearForensicsError,
  clearSelectedForensics,
  clearNotificationsError,
  clearShadowAgentsError,
  clearReportError,
} = aiGovernanceSlice.actions;

// =============================================================================
// SELECTORS
// =============================================================================

// Summary selectors
export const selectGovernanceSummary = (state: RootState) =>
  state.aiGovernance.summary;
export const selectSummaryLoading = (state: RootState) =>
  state.aiGovernance.summaryLoading;
export const selectSummaryError = (state: RootState) =>
  state.aiGovernance.summaryError;

// Agents selectors
export const selectGovernanceAgents = (state: RootState) =>
  state.aiGovernance.agents;
export const selectAgentsTotal = (state: RootState) =>
  state.aiGovernance.agentsTotal;
export const selectAgentsLoading = (state: RootState) =>
  state.aiGovernance.agentsLoading;
export const selectAgentsError = (state: RootState) =>
  state.aiGovernance.agentsError;
export const selectAgentFilters = (state: RootState) =>
  state.aiGovernance.agentFilters;

// Containment selectors
export const selectContainmentLoading = (state: RootState) =>
  state.aiGovernance.containmentLoading;
export const selectContainmentError = (state: RootState) =>
  state.aiGovernance.containmentError;
export const selectLastContainmentResponse = (state: RootState) =>
  state.aiGovernance.lastContainmentResponse;

// Forensics selectors
export const selectSelectedAgentForensics = (state: RootState) =>
  state.aiGovernance.selectedAgentForensics;
export const selectForensicsLoading = (state: RootState) =>
  state.aiGovernance.forensicsLoading;
export const selectForensicsError = (state: RootState) =>
  state.aiGovernance.forensicsError;

// Notifications selectors
export const selectNotifications = (state: RootState) =>
  state.aiGovernance.notifications;
export const selectNotificationsLoading = (state: RootState) =>
  state.aiGovernance.notificationsLoading;
export const selectNotificationsError = (state: RootState) =>
  state.aiGovernance.notificationsError;

// Shadow agents selectors
export const selectShadowAgents = (state: RootState) =>
  state.aiGovernance.shadowAgents;
export const selectShadowAgentsLoading = (state: RootState) =>
  state.aiGovernance.shadowAgentsLoading;
export const selectShadowAgentsError = (state: RootState) =>
  state.aiGovernance.shadowAgentsError;

// Reports selectors
export const selectGeneratedReports = (state: RootState) =>
  state.aiGovernance.generatedReports;
export const selectReportGenerating = (state: RootState) =>
  state.aiGovernance.reportGenerating;
export const selectReportError = (state: RootState) =>
  state.aiGovernance.reportError;

// =============================================================================
// MEMOIZED DERIVED SELECTORS (createSelector for computed state)
// =============================================================================

/** Select agents filtered by current agent filters (memoized) */
export const selectFilteredAgents = createSelector(
  [selectGovernanceAgents, selectAgentFilters],
  (agents, filters) => {
    let filtered = agents;
    if (filters.autonomy_level) {
      filtered = filtered.filter((a) => a.autonomy_level === filters.autonomy_level);
    }
    if (filters.risk_level) {
      filtered = filtered.filter((a) => a.ai_risk_level === filters.risk_level);
    }
    return filtered;
  },
);

/** Count of agents by autonomy level (memoized) */
export const selectAgentCountsByAutonomy = createSelector(
  [selectGovernanceAgents],
  (agents) => ({
    GREEN: agents.filter((a) => a.autonomy_level === 'GREEN').length,
    YELLOW: agents.filter((a) => a.autonomy_level === 'YELLOW').length,
    RED: agents.filter((a) => a.autonomy_level === 'RED').length,
  }),
);

/** Notifications grouped by status (memoized) */
export const selectNotificationsByStatus = createSelector(
  [selectNotifications],
  (notifications) => ({
    pending_review: notifications.filter((n) => n.status === 'pending_review'),
    approved: notifications.filter((n) => n.status === 'approved'),
    submitted: notifications.filter((n) => n.status === 'submitted'),
  }),
);

/** Count of pending notifications requiring action (memoized) */
export const selectPendingNotificationCount = createSelector(
  [selectNotifications],
  (notifications) => notifications.filter((n) => n.status === 'pending_review').length,
);

/** Shadow agents sorted by risk (memoized, highest risk first) */
export const selectShadowAgentsByRisk = createSelector(
  [selectShadowAgents],
  (agents) => {
    const riskOrder: Record<string, number> = {
      unacceptable: 0,
      high: 1,
      limited: 2,
      minimal: 3,
    };
    return [...agents].sort(
      (a, b) => (riskOrder[a.ai_risk_level] ?? 99) - (riskOrder[b.ai_risk_level] ?? 99),
    );
  },
);

export default aiGovernanceSlice.reducer;
